import {
  type HandleOfferResponseFn,
  type ListQualifiedProvidersFn,
  type OpportunitiesFeatureOptions,
} from '@/features/opportunities'
import { ConsultationModalViewModel } from '@/features/opportunities/ConsultationModal/ConsultationModalViewModel'
import { OpportunitiesApi } from '@/features/opportunities/api/opportunities-api'
import { WorkspacePostingsApi } from '@/features/opportunities/api/workspace-postings-api'
import { WorkspaceOpportunityInvitationStore } from '@/features/opportunities/invitations/WorkspaceOpportunityInvitationStore'
import { OpportunityOfferStore } from '@/features/opportunities/offers/OpportunityOfferStore'
import { OpportunityOffersViewModel } from '@/features/opportunities/offers/OpportunityOffersViewModel'
import { WorkspacePostingOpportunityStore } from '@/features/opportunities/workspace-postings-opportunities/WorkspacePostingOpportunityStore'
import { WorkspaceOpportunityInvitationsViewModel } from '@/features/opportunities/invitations/WorkspaceOpportunityInvitationsViewModel'
import { env } from '@/infra/env'
import { defer, merge, share } from 'rxjs'
import { shareLatest } from '@/utils/rx-util'
import { OpportunitiesRootViewModel } from './OpportunitiesRootViewModel'
import { OpportunityStore } from './OpportunityStore'

export function InternalOpportunitiesFeature(
  options: OpportunitiesFeatureOptions,
) {
  const api = new OpportunitiesApi(
    {
      baseURL: env.WORK_DOTNET_API,
      token$: options.token$,
    },
    options.workRealtime$,
  )

  const consultationModalVm = new ConsultationModalViewModel()

  const opportunityStore = new OpportunityStore(
    api,
    options.primeUser,
    options.primeWorkspace,
    options.currentMember$,
    options.pickupOpportunity,
    options.getCurrentUserWorkspaceMemberships,
  )

  const opportunityOfferStore = new OpportunityOfferStore(
    api,
    options.acceptOffer,
    options.primeUser,
    options.primeWorkspace,
    opportunityStore.canViewOpportunities$,
  )

  const workspacePostingsApi = new WorkspacePostingsApi({
    baseURL: env.IAM_API,
    token$: options.token$,
  })

  const workspacePostingOpportunityStore = new WorkspacePostingOpportunityStore(
    workspacePostingsApi,
    options.fetchUserData,
    options.currentUser$,
  )

  const workspaceOpportunityInvitationStore =
    new WorkspaceOpportunityInvitationStore(
      workspacePostingsApi,
      workspacePostingOpportunityStore.canViewOpportunities$,
    )

  const opportunityOffersVm = new OpportunityOffersViewModel(
    opportunityOfferStore,
    consultationModalVm,
    options.pickupConsultationJob,
    options.newAlert,
    options.navigate,
    options.getCurrentMember,
  )

  const workspaceOpportunityInvitationsVm =
    new WorkspaceOpportunityInvitationsViewModel(
      workspaceOpportunityInvitationStore,
      options.newAlert,
      options.createAlertErrorHandler,
    )

  const opportunitiesRootViewModel = new OpportunitiesRootViewModel(
    opportunityStore,
    options.fetchBookedConsultations,
    options.pickupConsultationJob,
    options.fetchProOpenJobCounts,
    options.getCurrentUserWorkspaceMemberships,
    options.listWorkspaceMemberships,
    options.getTeamMembersForWorkspaceMember,
    options.fetchUserData,
    options.getUserData,
    options.getCurrentMember,
    options.newAlert,
    options.createAlertRetryWrapper,
    options.createAlertErrorHandler,
    options.navigate,
    options.jobEvents$,
    options.currentMember$,
    consultationModalVm,
    workspacePostingOpportunityStore,
  )

  // The offer and invitation view models subscribe to the shared incoming
  // streams first, so a notification reads the updated buffer.
  const systemNotifications$ = merge(
    opportunitiesRootViewModel.systemNotifications$,
    opportunityOffersVm.systemNotifications$,
  ).pipe(share())

  const canViewOpportunities$ = defer(
    async () =>
      (await opportunityStore.probeCanViewOpportunities()) ||
      (await workspacePostingOpportunityStore.canViewWorkspaceOpportunities()),
  ).pipe(shareLatest())

  const handleOfferResponse: HandleOfferResponseFn = (action) =>
    opportunityOffersVm.handleOfferResponse(action)

  const listQualifiedProviders: ListQualifiedProvidersFn = (jobId, query) =>
    opportunityStore.listQualifiedProviders(jobId, query)

  return {
    // Internal
    opportunityStore,
    consultationModalVm,
    handleOfferResponse,
    listQualifiedProviders,
    canViewOpportunities$,
    workspacePostingOpportunityStore,
    opportunitiesRootViewModel,
    opportunityOffersVm,
    workspaceOpportunityInvitationsVm,
    systemNotifications$,
  }
}
